import React, { useEffect, useState } from "react";
import { useUserProfile } from "../../contexts/UserProfileContext";
import useWorkoutSettings from "../../hooks/useWorkoutSettings";
import DetailSheet from "../session/generate/DetailSheet";
import DraggableStepper from "../ui/draggable-stepper";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { ChipGrid, ChoiceChip, DeleteConfirm, Field, SwitchRow } from "./ProfileParts";
import { LOCATION_TYPES } from "./profileSummaries";

const EMPTY = {
  name: "",
  locationType: "gym",
  equipmentPresetId: null,
  address: "",
  notes: "",
  isDefault: false,
  transitionSeconds: null,
  blockSetupSeconds: null,
};

const formFrom = (place) => ({
  name: place.name || "",
  locationType: place.location_type || "other",
  equipmentPresetId: place.equipment_preset_id || null,
  address: place.address || "",
  notes: place.notes || "",
  isDefault: Boolean(place.is_default),
  transitionSeconds: place.transition_seconds ?? null,
  blockSetupSeconds: place.block_setup_seconds ?? null,
});

// Tags are left out, so the server keeps any a place already has.
const toApi = (form) => ({
  name: form.name.trim(),
  location_type: form.locationType,
  address: form.address.trim(),
  notes: form.notes.trim(),
  is_default: form.isDefault,
  equipment_preset_id: form.equipmentPresetId || null,
  transition_seconds: form.transitionSeconds || null,
  block_setup_seconds: form.blockSetupSeconds || null,
});

/**
 * One place you train (a location preset): its equipment set and its own
 * changeover times. Edits save when the sheet closes; a new place saves
 * with Add.
 */
const PlaceSheet = ({ placeId, onClose }) => {
  const {
    locationPresets = [],
    equipmentPresets = [],
    addLocationPreset,
    updateLocationPreset,
    deleteLocationPreset,
  } = useUserProfile();
  const [workoutSettings] = useWorkoutSettings();
  const open = placeId != null;
  const isNew = placeId === "new";

  const [form, setForm] = useState(EMPTY);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const place = locationPresets.find((p) => p.id === placeId);
    setForm(place ? formFrom(place) : EMPTY);
    setDirty(false);
    setError(null);
    setConfirming(false);
    // Only when a different place opens; later list updates must not reset the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId]);

  const set = (fields) => {
    setForm((f) => ({ ...f, ...fields }));
    setDirty(true);
    setError(null);
  };

  const run = async (action, failure) => {
    setBusy(true);
    try {
      await action();
      return true;
    } catch (err) {
      console.error(failure, err);
      setError(`${failure} Check your connection and try again.`);
      return false;
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (!isNew && dirty) {
      if (!form.name.trim()) {
        setError("Give it a name.");
        return;
      }
      if (!(await run(() => updateLocationPreset(placeId, toApi(form)), "Couldn't save."))) return;
    }
    onClose();
  };

  const add = async () => {
    if (!form.name.trim()) {
      setError("Give it a name.");
      return;
    }
    if (await run(() => addLocationPreset(toApi(form)), "Couldn't save.")) onClose();
  };

  const remove = async () => {
    if (await run(() => deleteLocationPreset(placeId), "Couldn't delete.")) onClose();
  };

  return (
    <DetailSheet
      open={open}
      onOpenChange={(next) => !next && close()}
      title={isNew ? "Add a place" : form.name || "Place"}
      footer={
        isNew ? (
          <Button type="button" className="h-12 w-full text-base font-semibold" onClick={add} disabled={busy}>
            Add
          </Button>
        ) : undefined
      }
    >
      <Field label="Name" htmlFor="place-name">
        <Input
          id="place-name"
          value={form.name}
          onChange={(e) => set({ name: e.target.value })}
          placeholder="e.g. MettleWorks"
        />
      </Field>
      <Field label="Type">
        <ChipGrid
          options={LOCATION_TYPES}
          value={form.locationType}
          onChange={(locationType) => set({ locationType })}
        />
      </Field>
      <Field
        label="Equipment here"
        hint={
          equipmentPresets.length
            ? "Choosing this place in Generate loads that equipment."
            : "Add an equipment set on Profile to link one here."
        }
      >
        {equipmentPresets.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <ChoiceChip
              selected={!form.equipmentPresetId}
              onClick={() => set({ equipmentPresetId: null })}
            >
              Not set
            </ChoiceChip>
            {equipmentPresets.map((ep) => (
              <ChoiceChip
                key={ep.id}
                selected={form.equipmentPresetId === ep.id}
                onClick={() => set({ equipmentPresetId: ep.id })}
              >
                {ep.name}
                <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                  {Array.isArray(ep.equipment) ? ep.equipment.length : 0}
                </span>
              </ChoiceChip>
            ))}
          </div>
        )}
      </Field>
      <Field label="Changeovers here" aside="optional">
        <div className="grid grid-cols-2 gap-3">
          <DraggableStepper
            label="Between exercises"
            value={form.transitionSeconds || 0}
            onChange={(v) => set({ transitionSeconds: v || null })}
            min={0}
            max={120}
            step={5}
            unit="s"
            offLabel={`Usual ${workoutSettings.defaultTransitionSeconds ?? 15} s`}
          />
          <DraggableStepper
            label="Between blocks"
            value={form.blockSetupSeconds || 0}
            onChange={(v) => set({ blockSetupSeconds: v || null })}
            min={0}
            max={300}
            step={15}
            unit="s"
            offLabel={`Usual ${workoutSettings.defaultBlockSetupSeconds ?? 45} s`}
          />
        </div>
      </Field>
      <Field label="Address" htmlFor="place-address" aside="optional">
        <Input
          id="place-address"
          value={form.address}
          onChange={(e) => set({ address: e.target.value })}
          placeholder="e.g. 12 Parramatta Rd"
        />
      </Field>
      <Field label="Notes" htmlFor="place-notes" aside="optional">
        <Input
          id="place-notes"
          value={form.notes}
          onChange={(e) => set({ notes: e.target.value })}
          placeholder="e.g. Open 24/7, low ceiling"
        />
      </Field>
      <SwitchRow
        id="place-default"
        label="Default place"
        description="Generate starts here until you choose another place."
        checked={form.isDefault}
        onCheckedChange={(isDefault) => set({ isDefault })}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
      {!isNew && (
        <DeleteConfirm
          name={form.name || "this place"}
          consequence="Sessions you've already built keep their equipment."
          confirming={confirming}
          onAsk={() => setConfirming(true)}
          onCancel={() => setConfirming(false)}
          onConfirm={remove}
          busy={busy}
        />
      )}
    </DetailSheet>
  );
};

export default PlaceSheet;
